import { ECreditApplicationStatus, EFinancingProgram } from '@if/core/types'
import { PageContainer } from '@if/mui'
import { CircleX } from 'lucide-react'
import { useMerchantFinancingConfigQuery } from '@src/data/api/configs-api/financing-config-api'
import { useCreditApplicationByIdQuery } from '@src/data/api/credit-api/credit-api'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ViewApplicationActions from './components/ViewApplicationActions'
import ViewApplicationSkeleton from './components/ViewApplicationSkeleton'
import ViewCreditApplicationBody from './components/ViewCreditApplicationBody'
import {
  canPollCreditApp,
  hasPendingAsyncChecks,
  isServerAnalysingCredit,
  isServerCreatingContract,
} from './credit-hooks'
import { ECreditApplicationStatusMessage, getCurrentStep, getStepStatus } from './viewCreditApp-selectors'

const creditAppParams = ['financingProgramId', 'id'] as const

const useCreditApplicationPrimary = ({ financingProgramId, id }: { financingProgramId: string; id: string }) => {
  const [shouldPoll, setShouldPoll] = useState(false)
  const creditApplication = useCreditApplicationByIdQuery(
    { creditApplicationId: id, financingProgramId: financingProgramId as EFinancingProgram },
    shouldPoll,
  )

  // Poll while the server is analysing the credit / creating the contract; stops once settled.
  // A step's checks additionally keep polling for as long as their result is missing, so their
  // pending banner always clears itself.
  useEffect(() => {
    const app = creditApplication.data
    setShouldPoll(
      !!app &&
        (hasPendingAsyncChecks(app) ||
          ((isServerAnalysingCredit(app) || isServerCreatingContract(app)) && canPollCreditApp(app))),
    )
  }, [creditApplication.data])

  return { creditApplication }
}

// Financing config loaded as secondary (gated by the PageContainer). Keyed on the application's own
// merchant, not the session's: a banner views its children's applications, and the endpoint rejects
// the pair when the banner itself doesn't support the child's program.
const useCreditApplicationSecondary = ({ creditApplication }: { creditApplication: CreditApplication }) => {
  const financingConfig = useMerchantFinancingConfigQuery(
    creditApplication.merchantId,
    creditApplication.financingProgramId,
  )
  return { financingConfig }
}

const ViewCreditApplicationPage = () => {
  const { t } = useTranslation()

  const terminalChip = (status: ECreditApplicationStatus) => ({
    label: t(`ECreditApplicationStatus.${status}`, { ns: 'enum' }),
    color: 'error' as const,
    icon: CircleX,
  })

  return (
    <PageContainer
      params={creditAppParams}
      usePrimary={useCreditApplicationPrimary}
      useSecondary={useCreditApplicationSecondary}
      loadingContent={<ViewApplicationSkeleton />}
      back={{ to: '/credits', label: t('viewCreditApplication.backToApplications') }}
      title={({ creditApplication }) => `${t('common.application')} #${creditApplication.referenceNumber}`}
      // Cancelled / Expired take over the chip with the app status in error red; an
      // active application keeps the current-step status chip (same enum registry as
      // the stage banner; a null status — e.g. Draft — yields undefined, which hides).
      chip={({ creditApplication }) => {
        if (
          creditApplication.status === ECreditApplicationStatus.Cancelled ||
          creditApplication.status === ECreditApplicationStatus.Expired
        ) {
          return terminalChip(creditApplication.status)
        }
        const status = getStepStatus(getCurrentStep(creditApplication), creditApplication)
        // Cast mirrors the resolver's non-optional PageTitleChip type: a null status
        // (e.g. Draft) yields undefined, which PageContainer renders as no chip.
        return (status ? { set: ECreditApplicationStatusMessage, value: status } : undefined) as {
          set: typeof ECreditApplicationStatusMessage
          value: ECreditApplicationStatusMessage
        }
      }}
      actions={({ creditApplication }) => <ViewApplicationActions creditApp={creditApplication} />}
    >
      {({ creditApplication, financingConfig }) => (
        <ViewCreditApplicationBody creditApplication={creditApplication} financingConfig={financingConfig} />
      )}
    </PageContainer>
  )
}

export default ViewCreditApplicationPage
